// The stock adjustments of the seven material edit screens (Keycap, Ink, KeycapPuller,
// ShippingBox, TransferFilm, Foam, CardboardBox). Each function takes the quantities the
// screen holds and the adjustment the admin typed into the field next to them (a string,
// as the number input gives it), and returns the quantities and the field afterwards,
// with the error the screen shows when a rule refuses the change (the field keeps the
// typed value then, for a correction). Only the Ink,
// KeycapPuller and ShippingBox screens let the admin adjust the reserved and the sold
// quantity by hand. freeStock serves the order list's ship dialog too (utils/orderList.js).
// stockQuantitiesBody is the quantities part of the seven screens' save (a copy each until
// chain 9, Oct 2026); the message their failed save shows is materialSaveError in utils.js.

export const freeStock = ({ stockQuantity, reservedQuantity }) =>
  Math.max((stockQuantity || 0) - (reservedQuantity || 0), 0); // how many pieces of free (un-reserved) stock are still available, of a material document or an edit screen's quantities: since Oct 2026 (user decision) the one form of the ship dialog's clamped copies for shipping boxes and keycap pullers, its cardboard and foam labels (unclamped until then) and this module's plain subtraction; the `|| 0` covers a document read lean without reservedQuantity, the clamp a reservation above the stock, which the backend's guards refuse

export const adjustStock = (
  { stockQuantity, reservedQuantity },
  stockAdjustment
) => {
  const adjustmentValue = Number(stockAdjustment);

  if (adjustmentValue === 0) {
    return { stockQuantity, stockAdjustment }; // If user entered zero, no update is needed
  }

  const targetStock = stockQuantity + adjustmentValue;

  // 1. Stock can never be below zero
  if (targetStock < 0) {
    return {
      stockQuantity,
      stockAdjustment,
      error: 'Stock cannot go below zero.',
    };
  }

  // 2. Stock must always be >= reserved (reserved is part of stock)
  if (targetStock < reservedQuantity) {
    return {
      stockQuantity,
      stockAdjustment,
      error: 'Stock cannot be lower than the reserved quantity.',
    };
  }

  return { stockQuantity: targetStock, stockAdjustment: 0 }; // update the stock, Reset the adjustment input
};

export const adjustScrap = (
  { stockQuantity, reservedQuantity, scrapQuantity },
  scrapAdjustment
) => {
  const adjustmentValue = Number(scrapAdjustment);

  if (adjustmentValue === 0) {
    return { stockQuantity, scrapQuantity, scrapAdjustment }; // If the user entered zero, there is nothing to apply
  }

  if (adjustmentValue > 0) {
    // Scrapping may **only** consume free stock (stock − reserved)
    const free = freeStock({ stockQuantity, reservedQuantity });

    if (adjustmentValue > free) {
      return {
        stockQuantity,
        scrapQuantity,
        scrapAdjustment,
        error:
          'Not enough free stock (stock − reserved) to scrap that many pieces.',
      };
    }

    return {
      scrapQuantity: scrapQuantity + adjustmentValue,
      stockQuantity: stockQuantity - adjustmentValue,
      scrapAdjustment: 0, // Reset the adjustment field afterward
    };
  }

  // User is trying to subtract from the scrapQuantity
  const absValue = Math.abs(adjustmentValue);
  if (scrapQuantity >= absValue) {
    return {
      scrapQuantity: scrapQuantity - absValue,
      stockQuantity: stockQuantity + absValue,
      scrapAdjustment: 0, // Reset the adjustment field afterward
    };
  }

  return {
    stockQuantity,
    scrapQuantity,
    scrapAdjustment, // kept for a correction, like every refusal (it was cleared before PR 28)
    error: 'Not enough scrapQuantity to remove that many scrap pieces.',
  };
};

export const adjustReserved = (
  { stockQuantity, reservedQuantity },
  reservedAdjustment
) => {
  const adjustmentValue = Number(reservedAdjustment);

  if (adjustmentValue === 0) {
    return { reservedQuantity, reservedAdjustment }; // No update needed
  }

  if (adjustmentValue > 0) {
    // reserve *without* touching stock; reserved must never exceed stock
    if (reservedQuantity + adjustmentValue <= stockQuantity) {
      return {
        reservedQuantity: reservedQuantity + adjustmentValue,
        reservedAdjustment: 0, // Reset input field
      };
    }

    return {
      reservedQuantity,
      reservedAdjustment, // kept for a correction (it was cleared before PR 28)
      error: 'Reserved quantity cannot exceed stock.',
    };
  }

  // Trying to unreserve items
  const absValue = Math.abs(adjustmentValue);
  if (reservedQuantity >= absValue) {
    return {
      reservedQuantity: reservedQuantity - absValue,
      reservedAdjustment: 0, // Reset input field
    };
  }

  return {
    reservedQuantity,
    reservedAdjustment, // kept for a correction (it was cleared before PR 28)
    error: 'Cannot unreserve more than what is reserved.',
  };
};

export const adjustSold = (
  { stockQuantity, reservedQuantity, soldQuantity },
  soldAdjustment
) => {
  const adjustmentValue = Number(soldAdjustment);

  if (adjustmentValue === 0) {
    return { stockQuantity, reservedQuantity, soldQuantity, soldAdjustment }; // Nothing to do
  }

  if (adjustmentValue > 0) {
    // Move items from reserved → sold, they leave stock forever. The reservation never exceeds the stock: adjustStock and adjustReserved refuse that on the screens, and the backend's reserve and sale helpers (utils.js) guard it; the save routes store what a screen sends, so only a direct database edit could break it (the stock check here went, user decision, Oct 2026)
    if (reservedQuantity >= adjustmentValue) {
      return {
        soldQuantity: soldQuantity + adjustmentValue,
        reservedQuantity: reservedQuantity - adjustmentValue,
        stockQuantity: stockQuantity - adjustmentValue,
        soldAdjustment: 0, // Reset the adjustment input
      };
    }

    return {
      stockQuantity,
      reservedQuantity,
      soldQuantity,
      soldAdjustment, // kept for a correction (it was cleared before PR 28)
      error: 'Not enough reserved items to mark as sold.',
    };
  }

  // Trying to undo sold items (move them back to reserved)
  const absValue = Math.abs(adjustmentValue);

  if (soldQuantity >= absValue) {
    return {
      soldQuantity: soldQuantity - absValue,
      reservedQuantity: reservedQuantity + absValue,
      stockQuantity: stockQuantity + absValue,
      soldAdjustment: 0, // Reset the adjustment input
    };
  }

  return {
    stockQuantity,
    reservedQuantity,
    soldQuantity,
    soldAdjustment, // kept for a correction (it was cleared before PR 28)
    error: 'Cannot subtract more than the sold quantity.',
  };
};

export const stockQuantitiesBody = ({
  stockQuantity,
  originalStockQuantity,
  reservedQuantity,
  originalReservedQuantity,
  scrapQuantity,
  originalScrapQuantity,
  soldQuantity,
  originalSoldQuantity,
}) => ({
  stockQuantity,
  originalStockQuantity,
  reservedQuantity,
  originalReservedQuantity,
  scrapQuantity,
  originalScrapQuantity,
  soldQuantity,
  originalSoldQuantity,
}); // the quantities a material edit screen saves, each beside the value the screen loaded: the material routes update the document only while its four quantities still equal the originals (the filter of their findOneAndUpdate), and answer 409 when another save changed them meanwhile. One place that names that contract, with its test; the screens still hold their eight values, so it saves no code (a chain 9 review)
